import heroImg from "@/assets/hero-premium-1024.webp";
import hero640 from "@/assets/hero-premium-640.webp";
import hero1024 from "@/assets/hero-premium-1024.webp";
import hero1600 from "@/assets/hero-premium-1600.webp";
import { trackEvent, pushDataLayerEvent } from "@/lib/analytics";
import { Check, FileText, Star } from "lucide-react";
import { Link } from "react-router-dom";

interface HeroSectionProps {
  onAction: (action: string) => void;
}

const trustPoints = ["С 1993 года", "Более 1000 клиентов", "Без предоплаты", "Бесплатный замер", "Установка за 3 дня", "Гарантия"];

const HeroSection = ({ onAction }: HeroSectionProps) => {
  return (
    <section className="relative overflow-hidden rounded-2xl md:rounded-3xl mx-4 md:mx-0 w-[calc(100%-2rem)] md:w-full min-h-[500px] md:min-h-0 md:h-[540px] lg:h-[580px] flex">
      <img
        src={heroImg}
        srcSet={`${hero640} 640w, ${hero1024} 1024w, ${hero1600} 1600w`}
        sizes="(max-width: 768px) 100vw, (max-width: 1280px) 100vw, 1400px"
        alt="Жалюзи под ваш интерьер — Zebra Jaluzi, Ташкент"
        className="absolute inset-0 w-full h-full object-cover"
        width={1600}
        height={900}
        fetchPriority="high"
        decoding="async"
      />
      {/* Layered overlays for text readability */}
      <div className="absolute inset-0" style={{ background: "var(--hero-overlay)" }} />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground/60 via-foreground/25 to-transparent md:via-foreground/15" />

      <div className="relative z-10 flex flex-col items-center md:items-start justify-center w-full px-6 md:px-12 lg:px-16 py-10 md:py-12 text-center md:text-left">
        <div className="w-full md:max-w-[560px]">
          <div
            className="inline-flex flex-row flex-wrap justify-center items-center gap-x-2 gap-y-1 mb-4 px-3.5 py-2 rounded-full bg-primary-foreground/15 backdrop-blur-md border border-primary-foreground/25 text-primary-foreground"
            aria-label="Клиенты рекомендуют Zebra Jaluzi"
          >
            <div className="flex items-center gap-1.5 text-[12px] md:text-[13px] font-semibold">
              <Star className="h-3.5 w-3.5 fill-current text-yellow-300" strokeWidth={0} aria-hidden="true" />
              <span className="font-normal opacity-90">Клиенты рекомендуют Zebra Jaluzi</span>
            </div>
            <div className="flex items-center gap-2 text-[10px] md:text-[11px] font-medium tracking-[0.12em] uppercase">
              <span className="hidden sm:block h-1 w-1 rounded-full bg-primary-foreground" />
              С 1993 года · Более 1000 клиентов
            </div>
          </div>
          <h1 className="text-[2rem] md:text-[2.75rem] lg:text-[3.25rem] font-bold text-primary-foreground mb-3 md:mb-4 leading-[1.08] tracking-tight drop-shadow-2xl">
            Жалюзи в Ташкенте от 52 000 сум
          </h1>
          <p className="text-[14px] md:text-base text-primary-foreground/90 mb-5 md:mb-6 leading-relaxed font-light max-w-[440px] mx-auto md:mx-0">
            День-Ночь, рулонные, плиссе, москитные сетки и рольставни. Бесплатный замер, подбор решения и монтаж под ключ.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={() => {
                trackEvent("click_price_pdf");
                pushDataLayerEvent("click_price_button", { button_text: "Расчёт за 2 минуты" });
                onAction("price");
              }}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-7 md:px-8 py-3.5 text-[14px] font-semibold text-primary-foreground shadow-2xl shadow-primary/40 transition-all active:scale-[0.97] md:hover:scale-[1.03] md:hover:shadow-primary/60"
            >
              Получить расчёт за 2 минуты
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </button>
            <Link
              to="/catalog"
              onClick={() => trackEvent("click_catalog_pdf", { source: "hero" })}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-primary-foreground/10 backdrop-blur-md border border-primary-foreground/30 px-7 py-3.5 text-[14px] font-semibold text-primary-foreground transition-all active:scale-[0.97] md:hover:bg-primary-foreground/20"
            >
              <FileText className="h-4 w-4" />
              Получить каталог PDF
            </Link>
          </div>
          <p className="mt-3 text-[11px] md:text-xs text-primary-foreground/80 font-light">
            Запишитесь сегодня — приедем уже завтра · Ответ за 15 минут
          </p>
          <div className="mt-5 md:mt-6 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 max-w-[520px]">
            {trustPoints.map((p) => (
              <div key={p} className="flex items-center gap-1.5 text-primary-foreground/90 text-[10px] md:text-[11px] font-medium">
                <Check className="h-3 w-3 text-primary-foreground shrink-0" strokeWidth={2.5} />
                {p}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
